namespace $.$$ {

	type $shm_echarts_type = 'line' | 'bar' | 'scatter' | 'pie'

	export class $shm_hitalama_map_widget_float_echarts extends $.$shm_hitalama_map_widget_float_echarts {

		@ $mol_mem
		chart() {
			return this.widget().Chart()?.remote() ?? null
		}

		values_title( next?: string ) {
			return this.chart()?.values_title( next ) ?? ''
		}

		setting< Value >( name: string, next: Value | undefined, fallback: Value ): Value {
			const prev = ( this.widget().Data()?.val() ?? {} ) as Record< string, Value >
			if( next === undefined ) return prev[ name ] ?? fallback

			this.widget().Data( null )?.val({ ... prev, [ name ]: next })
			return next
		}

		chart_type( next?: string ): $shm_echarts_type {
			return this.setting( 'chart_type', next as $shm_echarts_type | undefined, 'line' )
		}

		legend_show( next?: boolean ) {
			return this.setting( 'legend_show', next, true )
		}

		smooth( next?: boolean ) {
			return this.setting( 'smooth', next, false )
		}

		area( next?: boolean ) {
			return this.setting( 'area', next, false )
		}

		stacked( next?: boolean ) {
			return this.setting( 'stacked', next, false )
		}

		toolbox_show( next?: boolean ) {
			return this.setting( 'toolbox_show', next, true )
		}

		data_zoom_show( next?: boolean ) {
			return this.setting( 'data_zoom_show', next, false )
		}

		@ $mol_mem
		traversed() {
			if( this.chart()?.values().length ) return {
				by_group: new Map< string, Map< string, number > >,
				labels: new Set< string >,
				field_options: new Map< string, Set< string | number > >,
			}
			return this.chart()?.traversed() ?? {
				by_group: new Map< string, Map< string, number > >,
				labels: new Set< string >,
				field_options: new Map< string, Set< string | number > >,
			}
		}

		labels() {
			const chart = this.chart()
			if( chart?.values().length ) {
				const fields = chart.table_head()
				const axis_i = fields.indexOf( chart.axis() )
				if( axis_i >= 0 ) {
					const labels: string[] = []
					for( const row of chart.rows() ) {
						let included = true
						row.forEach( ( value, index )=> {
							included = included == false
								? false
								: chart.row_included([ fields[index], value ])
						} )
						if( !included ) continue

						const label = row[ axis_i ]
						if( label === null || label === undefined || label === '' ) continue
						labels.push( String( label ) )
					}
					if( labels.length ) return labels
				}
			}

			const labels = [ ... this.traversed().labels ].filter( label => label !== null && label !== undefined && label !== '' ).map( String )
			return labels.length ? labels : [ 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun' ]
		}

		names() {
			const values = this.chart()?.values() ?? []
			if( values.length ) return values.map( String )

			const names = [ ... this.traversed().by_group.keys() ].filter( Boolean )
			return names.length ? names : [ this.values_title() || 'Series' ]
		}

		series_values( name: string ) {
			const values = this.chart()?.values() ?? []
			if( values.length ) return this.series_values_column( name )

			const labels = this.labels()
			const by_label = this.traversed().by_group.get( name )
			if( by_label ) return labels.map( label => Number( by_label.get( label ) ?? 0 ) )
			return [ 150, 230, 224, 218, 135, 147, 260 ]
		}

		series_values_column( name: string ) {
			const labels = this.labels()
			const chart = this.chart()
			if( !chart ) return labels.map( ()=> '-' )
			const fields = chart.table_head()
			const axis_i = fields.indexOf( chart.axis() )
			const value_i = fields.indexOf( name )
			if( axis_i < 0 || value_i < 0 ) return labels.map( ()=> '-' )

			const by_label = new Map< string, number | '-' >
			for( const row of chart.rows() ) {
				let included = true
				row.forEach( ( value, index )=> {
					included = included == false
						? false
						: chart.row_included([ fields[index], value ])
				} )
				if( !included ) continue

				const label = String( row[ axis_i ] )
				const value = row[ value_i ]
				by_label.set(
					label,
					value === null || value === undefined || value === ''
						? '-'
						: Number( value ),
				)
			}

			return labels.map( label => by_label.get( label ) ?? '-' )
		}

		theme_option() {
			const lights = this.$.$mol_lights()

			return lights ? {
				color: [ '#2563eb', '#16a34a', '#dc2626', '#9333ea', '#ea580c', '#0891b2' ],
				text: '#1f2937',
				shade: '#6b7280',
				line: 'rgba( 107, 114, 128, .28 )',
				back: 'rgba( 255, 255, 255, .92 )',
				shadow: 'rgba( 15, 23, 42, .12 )',
			} : {
				color: [ '#60a5fa', '#4ade80', '#f87171', '#c084fc', '#fb923c', '#22d3ee' ],
				text: '#e5e7eb',
				shade: '#9ca3af',
				line: 'rgba( 156, 163, 175, .24 )',
				back: 'rgba( 17, 24, 39, .94 )',
				shadow: 'rgba( 0, 0, 0, .35 )',
			}
		}

		option() {
			const type = this.chart_type()
			const labels = this.labels()
			const names = this.names()
			const theme = this.theme_option()
			const axis = {
				axisLine: { lineStyle: { color: theme.line } },
				axisTick: { lineStyle: { color: theme.line } },
				axisLabel: { color: theme.shade },
				splitLine: { lineStyle: { color: theme.line } },
			}
			const common = {
				color: theme.color,
				textStyle: { color: theme.text },
				legend: {
					show: this.legend_show(),
					top: 4,
					textStyle: { color: theme.text },
				},
				grid: { left: 48, right: 24, top: this.legend_show() ? 48 : 24, bottom: this.data_zoom_show() ? 56 : 32 },
				tooltip: {
					trigger: type == 'pie' ? 'item' : 'axis',
					backgroundColor: theme.back,
					borderColor: theme.line,
					textStyle: { color: theme.text },
					extraCssText: `box-shadow: 0 0.25rem 1rem ${ theme.shadow };`,
				},
				toolbox: {
					show: this.toolbox_show(),
					right: 8,
					iconStyle: { borderColor: theme.shade },
					emphasis: { iconStyle: { borderColor: theme.text } },
					feature: {
						saveAsImage: {},
						restore: {},
						dataZoom: {},
					},
				},
			}

			if( type == 'pie' ) {
				const values = this.series_values( names[0] )
				return {
					... common,
					series: [{
						name: names[0],
						type,
						radius: [ '35%', '68%' ],
						center: [ '50%', '55%' ],
						data: labels.map( ( label, index )=> ({ name: label, value: values[ index ] }) ),
					}],
				}
			}

			const series = names.map( name => {
				const item: any = {
					name,
					type,
					data: this.series_values( name ),
					smooth: type == 'line' && this.smooth(),
				}
				if( this.stacked() ) item.stack = 'total'
				if( type == 'line' && this.area() ) item.areaStyle = {}
				return item
			} )

			return {
				... common,
				dataZoom: this.data_zoom_show() ? [{ type: 'inside' }, { type: 'slider' }] : [],
				xAxis: { ... axis, type: 'category', data: labels },
				yAxis: { ... axis, type: 'value' },
				series,
			}
		}

		@ $mol_action
		settings_add() {
			this.widget().settings_widget_enabled( true )
			this.Map_page().contextmenu_showed( false )
		}

		@ $mol_action
		filter_add( name: string ) {
			const chart = this.chart()
			if( !chart ) return
			const pos = this.Map_page().get_pointer_pos()
			const widget = this.map().widget_add( 'chart_filter', pos, 180, 290 )
			widget?.text( name )
			widget?.Chart( null )?.remote( chart )
			this.Map_page().contextmenu_showed( false )
		}

		filter_buttons() {
			return this.chart()?.table_head().map( name => this.Filter_add( name ) ) ?? []
		}

		filter_name( id: any ): string {
			return id
		}

	}

}
